import React, { useEffect, useState } from "react";
import { db, storage, auth } from "./firebase";
import { collection, getDocs, addDoc, serverTimestamp, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import "./VoiceStory.css";

export default function VoiceStory() {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useOutletContext();
  const setIsLoginOpen = context?.setIsLoginOpen;

  // The voice object passed via navigate state from Blogs.js
  const voice = location.state?.voice;

  useEffect(() => {
    // Temporarily disabled as per request - redirect everyone to blog for now
    navigate("/blog");
    
    /* Original logic:
    // If accessed directly without state, redirect back to blogs
    if (!voice) {
      navigate("/blog");
    }
    // Scroll to top on load
    window.scrollTo({ top: 0, behavior: "smooth" });
    */
  }, [voice, navigate]);

  const [similarVoices, setSimilarVoices] = useState([]);
  const [categories, setCategories] = useState(["All", "Resilience", "Love", "Hope", "Courage", "Transformation"]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [modalStep, setModalStep] = useState(1);
  const [formData, setFormData] = useState({ title: "", story: "", name: "", location: "" });
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  useEffect(() => {
    const fetchSimilarVoices = async () => {
      try {
        const voicesSnapshot = await getDocs(collection(db, "voices"));
        const adminVoices = voicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const userVoicesQuery = query(collection(db, "user_submitted_voices"), where("status", "==", "approved"));
        const userVoicesSnapshot = await getDocs(userVoicesQuery);
        const userVoices = userVoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), isUserSubmitted: true }));
        
        const allVoices = [...adminVoices, ...userVoices];
        const filtered = allVoices.filter(v => v.id !== voice?.id);
        setSimilarVoices(filtered);
        
        // Categories are initialized in useState
      } catch (error) {
        console.error("Error fetching similar voices:", error);
      }
    };
    if (voice) {
      fetchSimilarVoices();
    }
  }, [voice]);

  const displayVoices = similarVoices.filter(v => {
    // Check if the voice text or header contains the active category
    const matchesCategory = activeCategory === "All" || 
      (v.category && v.category.toLowerCase().includes(activeCategory.toLowerCase())) ||
      (v.voiceHeader && v.voiceHeader.toLowerCase().includes(activeCategory.toLowerCase())) ||
      (v.text && v.text.toLowerCase().includes(activeCategory.toLowerCase())) ||
      (v.location && v.location.toLowerCase().includes(activeCategory.toLowerCase()));
      
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchLower || 
      (v.name && v.name.toLowerCase().includes(searchLower)) ||
      (v.voiceHeader && v.voiceHeader.toLowerCase().includes(searchLower)) ||
      (v.category && v.category.toLowerCase().includes(searchLower)) ||
      (v.text && v.text.toLowerCase().includes(searchLower));
      
    return matchesCategory && matchesSearch;
  });

  const handleSubmitStory = async () => {
    if (!auth.currentUser) {
      setSubmitMessage("Please log in to submit your story.");
      if (setIsLoginOpen) setIsLoginOpen(true);
      return;
    }

    if (!formData.story.trim() || !formData.title.trim()) {
      setSubmitMessage("Please provide at least a title and your story.");
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      let thumbnailUrl = "";
      if (imageFile) {
        const imgRef = ref(storage, `voicesimage/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imgRef, imageFile);
        thumbnailUrl = await getDownloadURL(imgRef);
      }

      await addDoc(collection(db, "user_submitted_voices"), {
        id: Date.now(),
        name: formData.name || "Anonymous",
        location: formData.location || "Community",
        category: selectedThread || "",
        voiceHeader: formData.title,
        text: formData.story,
        thumbnailUrl: thumbnailUrl,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setSubmitMessage("Your story has been submitted successfully and is pending review!");
      setTimeout(() => {
        setIsShareModalOpen(false);
        setModalStep(1);
        setSelectedThread(null);
        setFormData({ title: "", story: "", name: "", location: "" });
        setImageFile(null);
        setSubmitMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error submitting story:", error);
      setSubmitMessage("Error submitting story. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!voice) return null;

  return (
    <div className="voice-story-wrapper">

      <div className="voice-story-page">
      
      {/* Back Button */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 5%', marginBottom: '24px' }}>
        <button 
          onClick={() => navigate("/blog")} 
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '8px', 
            color: '#003049', 
            fontSize: '15px', 
            fontWeight: '600', 
            padding: '8px 0',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          Back 
        </button>
      </div>

      <div className="story-header-flex">
        <div className="story-header-left">
          <div className="story-header-subtitle">
            Real Stories &middot; Real Journeys &middot; Real Transformation
          </div>
        </div>
        <div className="story-header-right">
          <p className="story-header-desc">
            Stories that remind us of our posibilities
          </p>
        </div>
      </div>

      {/* Hero Image Section */}
      {(voice.thumbnailUrl || voice.img) && (
        <div className="voice-story-hero-container">
          <img
            src={voice.thumbnailUrl || voice.img}
            alt={voice.name}
            className="voice-story-hero-img"
          />
        </div>
      )}

      {/* Content Section */}
      <div className="voice-story-content">
        <h1 className="voice-story-title">{voice.name}</h1>
        
        {voice.voiceHeader && (
          <p className="voice-story-header-text">{voice.voiceHeader}</p>
        )}

        <div className="voice-story-body">
          <p style={{ whiteSpace: 'pre-wrap' }}>{voice.text || "Story content coming soon..."}</p>
        </div>

        <p className="voice-story-footer-text">This is my story.</p>
{/*         
        <div 
          className="voice-story-back-btn" 
          onClick={() => navigate("/blog")}
        >
          Go Back
        </div> */}

        {voice.location && (
          <div className="voice-story-tags">
            {voice.location}
          </div>
        )}
      </div>

      {/* Find Your Story Section */}
      <div className="similar-voices-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div>
            <div style={{fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', color: '#003049', marginBottom: '8px', fontWeight: 'bold'}}>BROWSE BY THREAD</div>
            <h2 style={{ fontSize: '36px', fontFamily: "'Syne', sans-serif", margin: '0 0 24px 0', color: '#003049' }}>Find your story</h2>
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '30px',
                    backgroundColor: activeCategory === cat ? '#003049' : 'transparent',
                    color: activeCategory === cat ? '#fff' : '#003049',
                    border: '1px solid #003049',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {cat === "All" && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>}
                  {cat === "Love" && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>}
                  {cat === "Hope" && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>}
                  {cat === "Resilience" && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>}
                  {cat === "Courage" && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>}
                  {cat === "Transformation" && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6M21 12A9 9 0 0 0 6 5.3L3 8M21 22v-6h-6M3 12a9 9 0 0 0 15 6.7l3-2.7"></path></svg>}
                  {cat}
                </button>
              ))}
            </div>
          </div>
          
          <div style={{ position: 'relative', marginTop: '20px' }}>
            <svg style={{position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5}} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input 
              type="text" 
              placeholder="Search stories or authors..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '12px 16px 12px 42px',
                borderRadius: '30px',
                border: '1px solid #003049',
                background: 'transparent',
                color: '#003049',
                width: '300px',
                outline: 'none',
                maxWidth: '100%'
              }}
            />
          </div>
        </div>

        {displayVoices.length > 0 ? (
          <div className="similar-voices-grid">
            {displayVoices.map((v) => {
              // Determine tag for corner based on content matching
              let cardTag = null;
              let tagStyle = { background: 'rgba(255, 255, 255, 0.9)', color: '#003049' };
              let tagIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>;

              const searchStr = ((v.category || "") + " " + (v.voiceHeader || "") + " " + (v.text || "") + " " + (v.location || "")).toLowerCase();
              if (searchStr.includes("love")) {
                cardTag = "Love";
                tagStyle = { background: '#FCE4EC', color: '#D81B60' };
                tagIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>;
              } else if (searchStr.includes("hope")) {
                cardTag = "Hope";
                tagStyle = { background: '#E8F5E9', color: '#2E7D32' };
                tagIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>;
              } else if (searchStr.includes("courage")) {
                cardTag = "Courage";
                tagStyle = { background: '#FFF3E0', color: '#E65100' };
                tagIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>;
              } else if (searchStr.includes("transformation")) {
                cardTag = "Transformation";
                tagStyle = { background: '#F3E5F5', color: '#6A1B9A' };
                tagIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6M21 12A9 9 0 0 0 6 5.3L3 8M21 22v-6h-6M3 12a9 9 0 0 0 15 6.7l3-2.7"></path></svg>;
              } else if (searchStr.includes("resilience")) {
                cardTag = "Resilience";
                tagStyle = { background: '#FBE9E7', color: '#D84315' };
                tagIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>;
              } else if (v.category) {
                cardTag = v.category;
              }
              
              return (
              <div 
                key={v.id} 
                className="similar-voice-card"
                onClick={() => navigate('/voice-story', { state: { voice: v } })}
                style={{ position: 'relative' }}
              >
                {cardTag && (
                  <div style={{ position: 'absolute', top: '12px', left: '12px', background: tagStyle.background, padding: '4px 12px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '600', color: tagStyle.color, display: 'flex', alignItems: 'center', gap: '6px', zIndex: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    {tagIcon}
                    {cardTag}
                  </div>
                )}
                <img src={v.thumbnailUrl || v.img || "/blogsimage/headermay26blog.jpeg"} alt={v.name} className="similar-voice-img" />
                <div className="similar-voice-info">
                  <div className="similar-voice-header">
                    <h3 className="similar-voice-name">{v.name}</h3>
                    <div className="similar-voice-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 16 16 12 12 8"></polyline>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                  <p className="similar-voice-desc">{v.voiceHeader || (v.text ? v.text.substring(0, 80) + '...' : '')}</p>
                  <p className="similar-voice-tags">{v.location}</p>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '40px 0', textAlign: 'center', opacity: 0.7 }}>
            No stories found matching your criteria.
          </div>
        )}
      </div>

      {/* Call to Action Section */}
      <div className="voice-story-cta-section">
        <div className="voice-story-cta-card">
          <div className="voice-story-cta-left">
            <p className="voice-story-cta-subtitle">YOUR STORY IS WAITING</p>
            <h2 className="voice-story-cta-title">
              What happened<br />to you <span>matters.</span>
            </h2>
            <p className="voice-story-cta-desc">
              Every story on this platform was written by someone who wasn't sure anyone would want to read it. They were wrong.<br />Share yours — anonymously if you need to.
            </p>
          </div>
          <div className="voice-story-cta-right">
            <button className="voice-story-cta-btn primary" onClick={() => setIsShareModalOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
              Share Your Story
            </button>
           
          </div>
        </div>
      </div>
      
      {/* Share Modal */}
      {isShareModalOpen && (
        <div className="voice-story-modal-overlay">
          <div className="voice-story-modal">
            <button className="voice-story-modal-close" onClick={() => { setIsShareModalOpen(false); setModalStep(1); }}>✕</button>
            
            {modalStep === 1 ? (
              <>
                <div className="voice-story-modal-progress">
                  <div className="voice-story-modal-progress-filled"></div>
                  <div className="voice-story-modal-progress-empty"></div>
                </div>

                <p className="voice-story-modal-step">STEP 1 OF 2</p>
                <h2 className="voice-story-modal-title">Your story matters.</h2>
                <p className="voice-story-modal-desc">Choose the thread your story belongs to. There is no wrong answer.</p>

                <div className="voice-story-modal-options">
                  <div 
                    className={`voice-story-modal-option ${selectedThread === 'Resilience' ? 'active' : ''}`}
                    onClick={() => setSelectedThread('Resilience')}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v10"></path><path d="M12 12l4-4"></path><path d="M12 12L8 8"></path><path d="M22 20H2"></path></svg>
                    <span>Resilience</span>
                  </div>
                  <div 
                    className={`voice-story-modal-option ${selectedThread === 'Love' ? 'active' : ''}`}
                    onClick={() => setSelectedThread('Love')}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                    <span>Love</span>
                  </div>
                  <div 
                    className={`voice-story-modal-option ${selectedThread === 'Hope' ? 'active' : ''}`}
                    onClick={() => setSelectedThread('Hope')}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    <span>Hope</span>
                  </div>
                  <div 
                    className={`voice-story-modal-option ${selectedThread === 'Courage' ? 'active' : ''}`}
                    onClick={() => setSelectedThread('Courage')}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    <span>Courage</span>
                  </div>
                  <div 
                    className={`voice-story-modal-option ${selectedThread === 'Transformation' ? 'active' : ''}`}
                    onClick={() => setSelectedThread('Transformation')}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 12l-4-4-4 4M12 8v8"></path></svg>
                    <span>Transformation</span>
                  </div>
                </div>

                <button 
                  className="voice-story-modal-continue" 
                  disabled={!selectedThread}
                  onClick={() => setModalStep(2)}
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <div className="voice-story-modal-progress">
                  <div className="voice-story-modal-progress-filled"></div>
                  <div className="voice-story-modal-progress-filled"></div>
                </div>

                <p className="voice-story-modal-step">STEP 2 OF 2 &middot; {selectedThread}</p>
                <h2 className="voice-story-modal-title">Tell us what happened.</h2>
                <p className="voice-story-modal-desc">Write in your own voice. You can be anonymous.</p>

                <div className="voice-story-modal-form">
                  <input type="text" placeholder="A title for your story" className="voice-story-modal-input" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                  <textarea placeholder="Write your story here. There is no minimum. There is no template." className="voice-story-modal-textarea" value={formData.story} onChange={e => setFormData({...formData, story: e.target.value})}></textarea>
                  <input type="text" placeholder="Your name (or 'Anonymous')" className="voice-story-modal-input" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                  <input type="text" placeholder="Your location (optional)" className="voice-story-modal-input" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} />
                  
                  <div className="voice-story-modal-file-upload">
                    <label>Upload an image (optional)</label>
                    <input type="file" accept="image/png, image/jpeg, image/jpg, image/avif, image/webp" className="voice-story-modal-input file-input" onChange={e => setImageFile(e.target.files[0])} />
                  </div>
                </div>

                {submitMessage && (
                  <p style={{ color: submitMessage.includes("Error") ? 'red' : 'green', fontSize: '14px', marginBottom: '16px' }}>{submitMessage}</p>
                )}

                <div className="voice-story-modal-actions">
                  <button className="voice-story-modal-back" onClick={() => setModalStep(1)} disabled={isSubmitting}>Back</button>
                  <button className="voice-story-modal-submit" onClick={handleSubmitStory} disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit Your Story"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      </div>
    </div>
  );
}
